import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Input, Wordmark } from '@novaquantis/design-system';
import { useValidateResetToken, useResetPasswordMutation } from '../queries/auth';

const PAGE_CLASS =
  'flex flex-1 flex-col items-center justify-center gap-6 bg-background bg-[image:var(--nq-canvas-glow)] px-10 py-10';
const CARD_CLASS =
  'w-full max-w-[420px] rounded-2xl border border-[color:var(--border-slate)] bg-[color:var(--nq-surface-card-2)] px-11 py-9 shadow-[var(--shadow-card-soft),var(--inset-card-glow)]';
const HEADER_CLASS = 'mb-5 text-center';
const TITLE_CLASS =
  'mb-2 font-display text-2xl font-bold tracking-tight text-secondary [text-shadow:var(--glow-gold-text)]';
const SUBTITLE_CLASS = 'text-sm leading-normal text-[color:var(--text-secondary)]';
const ERROR_CLASS =
  'mb-4 rounded-md border border-negative/35 bg-negative/15 px-4 py-3 text-sm text-[color:var(--nq-down-soft)]';
const SUCCESS_CLASS =
  'mb-4 rounded-md border border-positive/35 bg-positive/12 px-4 py-3 text-sm text-[color:var(--nq-up-bright)]';

const ResetPasswordPage = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const { data: tokenData, isPending: validating, error: validateError } = useValidateResetToken(token);
  const resetMutation = useResetPasswordMutation();

  const email = tokenData?.valid ? (tokenData.email ?? '') : '';
  const tokenError = validateError
    ? ((validateError as { response?: { data?: { error?: string } } }).response?.data?.error ?? 'Invalid or expired reset link')
    : tokenData && !tokenData.valid
      ? 'Invalid or expired reset link'
      : null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (newPassword !== confirmPassword) {
      setFormError('Passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      setFormError('Password must be at least 6 characters');
      return;
    }

    try {
      await resetMutation.mutateAsync({ token: token!, newPassword, confirmPassword });
      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err: unknown) {
      setFormError(
        (err as { response?: { data?: { error?: string } } }).response?.data?.error ||
          'Failed to reset password',
      );
    }
  };

  const brand = (
    <div className="flex flex-col items-center gap-3">
      <img
        src="/assets/logo-bull-trim.png"
        alt="NovaQuantis"
        className="h-[72px] w-auto drop-shadow-[0_8px_24px_rgba(37,180,224,0.3)]"
      />
      <Wordmark size={28} />
    </div>
  );

  if (validating) {
    return (
      <div className={PAGE_CLASS}>
        {brand}
        <div className={CARD_CLASS}>
          <div className={HEADER_CLASS}>
            <h1 className={TITLE_CLASS}>Validating reset link</h1>
            <p className={SUBTITLE_CLASS}>Please wait while we validate your reset link.</p>
          </div>
        </div>
      </div>
    );
  }

  if (tokenError) {
    return (
      <div className={PAGE_CLASS}>
        {brand}
        <div className={CARD_CLASS}>
          <div className={HEADER_CLASS}>
            <h1 className={TITLE_CLASS}>Invalid reset link</h1>
            <p className={ERROR_CLASS}>{tokenError}</p>
          </div>
          <Button variant="glow" fullWidth onClick={() => navigate('/login')}>
            Go to login
          </Button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className={PAGE_CLASS}>
        {brand}
        <div className={CARD_CLASS}>
          <div className={HEADER_CLASS}>
            <h1 className={TITLE_CLASS}>Password reset</h1>
            <p className={SUCCESS_CLASS}>
              Your password has been reset successfully. Redirecting to login…
            </p>
          </div>
        </div>
      </div>
    );
  }

  const loading = resetMutation.isPending;

  return (
    <div className={PAGE_CLASS}>
      {brand}
      <div className={CARD_CLASS}>
        <div className={HEADER_CLASS}>
          <h1 className={TITLE_CLASS}>Reset your password</h1>
          <p className={SUBTITLE_CLASS}>Account: {email}</p>
        </div>

        <form onSubmit={handleSubmit} autoComplete="on">
          <Input
            label="New password"
            id="newPassword"
            name="new-password"
            type="password"
            value={newPassword}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewPassword(e.target.value)}
            autoComplete="new-password"
            required
            placeholder="Enter new password"
            minLength={6}
            disabled={loading}
            variant="onDark"
          />

          <Input
            label="Confirm password"
            id="confirmPassword"
            name="confirm-password"
            type="password"
            value={confirmPassword}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfirmPassword(e.target.value)}
            autoComplete="new-password"
            required
            placeholder="Confirm new password"
            minLength={6}
            disabled={loading}
            variant="onDark"
          />

          {formError && <div className={ERROR_CLASS}>{formError}</div>}

          <Button type="submit" disabled={loading} fullWidth variant="glow">
            {loading ? 'Resetting password…' : 'Reset password'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
